<template>
  <div
    class="message-box"
    :class="{
      'message-box__inline': inline,
    }"
    :style="{
      '--message-box-text': messageBoxConfig.color,
      '--message-box-background': messageBoxConfig.backgroundColor,
    }"
  >
    <i
      class="message-box__icon"
      :class="messageBoxConfig.icon"
    />
    <div class="message-box__text-container">
      <div class="message-box__summary">
        {{ summary }}
      </div>
      <div class="message-box__details">
        <slot>
          {{ details }}
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
} from 'vue';

import {
  TSeverity,
} from '../../types/notification.ts';

const props = defineProps<{
  summary: string,
  severity: TSeverity,
  details?: string,
  inline?: boolean,
}>();

type TMessageBoxConfig = {
  icon: string,
  color: string,
  backgroundColor: string,
};
const messageBoxConfig = computed<TMessageBoxConfig>(() => {
  let exhaustiveCheck: never;
  switch (props.severity) {
    case 'info':
      return {
        icon: 'pi pi-info-circle',
        color: 'var(--color-123)',
        backgroundColor: 'var(--color-116)',
      };
    case 'warning':
      return {
        icon: 'pi pi-exclamation-triangle',
        color: 'var(--color-142)',
        backgroundColor: 'var(--color-136)',
      };
    case 'danger':
      return {
        icon: 'pi pi-times-circle',
        color: 'var(--color-222)',
        backgroundColor: 'var(--color-215)',
      };
    default:
      exhaustiveCheck = props.severity;
      throw new Error(`Missing implementation for severity ${exhaustiveCheck}.`);
  }
});
</script>

<style scoped lang="scss">
@use '../../styles/typography.scss';

$message-box-icon-size: 1.6rem;

.message-box {
  display: flex;
  gap: var(--gap-union);
  background-color: var(--message-box-background);
  color: var(--message-box-text);
  padding: var(--gap);
  border-radius: 6px;
  text-align: start;

  &__inline {
    display: flex;
    flex-direction: row;

    .message-box__text-container {
      flex-direction: row;
      align-items: flex-end;
      gap: var(--gap-union);
    }
  }

  &__icon {
    grid-area: icon;
    font-size: $message-box-icon-size;
  }

  &__text-container {
    display: flex;
    flex-direction: column;
  }

  &__summary {
    @include typography.h5;

    grid-area: summary;
    line-height: $message-box-icon-size;
  }

  &__details {
    grid-area: details;
  }
}
</style>
